import { Tabs, TabsContentProps, useTabsContext } from '@chakra-ui/react';
import { forwardRef, useEffect, useState } from 'react';
import { useMTabsContext } from './MTabsContext';

export interface MTabPanelProps extends Omit<TabsContentProps, 'value'> {
  /** Injected positionally by the parent `MTabPanels` — not usually set directly. */
  value?: string;
}

/*
 * v3's `Tabs.Content` has no `isLazy`/`unmountOnExit`-equivalent prop at
 * all (its Ark props are just `{ value }` — visibility is CSS-driven, every
 * panel's children mount immediately regardless). `isLazy` (v2's Root-level
 * prop, threaded down via `MTabsContext` — see MTabs.tsx) is reimplemented
 * by hand here: children don't render until this panel is first the active
 * one, then stay mounted (matching v2's actual default `isLazy` behavior —
 * mount-once-then-keep, not remount-every-switch).
 */
export const MTabPanel = forwardRef<HTMLDivElement, MTabPanelProps>(
  ({ value, children, ...rest }, ref) => {
    const { isLazy } = useMTabsContext();
    const { value: activeValue } = useTabsContext();
    const isActive = activeValue === (value ?? '');
    const [hasBeenActive, setHasBeenActive] = useState(!isLazy);

    useEffect(() => {
      if (isActive) {
        setHasBeenActive(true);
      }
    }, [isActive]);

    return (
      <Tabs.Content ref={ref} value={value ?? ''} {...rest}>
        {hasBeenActive ? children : null}
      </Tabs.Content>
    );
  },
);

MTabPanel.displayName = 'MTabPanel';

export default MTabPanel;
